export const machineEngChartData = (data, keyName) => {
  // Validate keyName
  const validKeys = [
    "Actual_Progress",
    "Hours_of_Block_Demanded",
    "Hours_of_Block_Granted",
    "No_Of_Blocks_Demanded",
    "No_Of_Blocks_Granted",
    "Short_Fall_In_Progress",
    "Stipulated_Progress",
  ];
  if (!validKeys.includes(keyName)) {
    throw new Error(`Invalid keyName. Must be one of: ${validKeys.join(", ")}`);
  }

  // Get unique financial years and sort them
  const financialYears = Object.keys(data).sort((a, b) => {
    const [startA] = a.split("-").map(Number);
    const [startB] = b.split("-").map(Number);
    return startA - startB;
  });

  // Get unique products (ASSETs)
  const products = new Set();
  for (const year in data) {
    data[year].forEach((entry) => products.add(entry.ASSET));
  }
  const productList = Array.from(products).sort();

  // Initialize series object
  const series = productList.map((product) => ({
    product,
    ...Object.fromEntries(financialYears.map((year) => [year, 0])),
  }));

  // Aggregate data
  for (const year in data) {
    data[year].forEach((entry) => {
      const productIndex = series.findIndex((s) => s.product === entry.ASSET);
      if (productIndex !== -1) {
        const value = [
          "Hours_of_Block_Demanded",
          "Hours_of_Block_Granted",
          "No_Of_Blocks_Demanded",
          "No_Of_Blocks_Granted",
        ].includes(keyName)
          ? Number(entry[keyName])
          : parseFloat(entry[keyName]) || 0;
        series[productIndex][year] += value;
      }
    });
  }

  // Sort series by total descending
  series.sort((a, b) => {
    const sumA = financialYears.reduce((acc, year) => acc + (a[year] || 0), 0);
    const sumB = financialYears.reduce((acc, year) => acc + (b[year] || 0), 0);
    return sumB - sumA; // descending order
  });

  return {
    dimensions: ["product", ...financialYears],
    series,
  };
};

export function groupByFinancialYear(data) {
  const result = {};

  data.forEach((item) => {
    const date = new Date(item.reportDate);
    const year = date.getFullYear();
    const month = date.getMonth(); // Jan = 0, Feb = 1, ..., Dec = 11

    // Financial Year: April (3) to March (2)
    const fyStartYear = month >= 3 ? year : year - 1;
    const fyLabel = `${fyStartYear}-${fyStartYear + 1}`;

    if (!result[fyLabel]) {
      result[fyLabel] = [];
    }

    result[fyLabel].push(item);
  });

  return result;
}

// /////////////////////////////////////////////////////////////////////////////////
export function groupByFinancialYearDateWise(data, s, e) {
  const result = {};

  const curStart = new Date(s);
  const curEnd = new Date(e);

  // Current FY calculation
  const curFYStartYear =
    curStart.getMonth() >= 3
      ? curStart.getFullYear()
      : curStart.getFullYear() - 1;

  const prevFYStartYear = curFYStartYear - 1;

  // Previous FY same timeline
  const prevStart = new Date(curStart);
  prevStart.setFullYear(prevStart.getFullYear() - 1);

  const prevEnd = new Date(curEnd);
  prevEnd.setFullYear(prevEnd.getFullYear() - 1);

  const format = (d) =>
    `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1,
    ).padStart(2, "0")}-${d.getFullYear()}`;

  const buckets = {
    [`${prevFYStartYear}-${prevFYStartYear + 1} (${format(
      prevStart,
    )} / ${format(prevEnd)})`]: [],
    [`${curFYStartYear}-${curFYStartYear + 1} (${format(
      curStart,
    )} / ${format(curEnd)})`]: [],
  };

  data.forEach((item) => {
    const d = new Date(item.reportDate);

    if (d >= prevStart && d <= prevEnd) {
      buckets[Object.keys(buckets)[0]].push(item);
    } else if (d >= curStart && d <= curEnd) {
      buckets[Object.keys(buckets)[1]].push(item);
    }
  });

  return buckets;
}

export function sumDataByKeys(data, keysConfig) {
  //ex data = [{},{},{}]
  const result = {};

  // Convert HH:MM:SS to seconds
  const timeToSeconds = (timeStr) => {
    const [h, m, s] = timeStr.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  };

  // Convert seconds to HH:MM:SS
  const secondsToTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, "0");
    const m = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const s = (totalSeconds % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  for (const [key, value] of Object.entries(keysConfig)) {
    let sum = 0;

    for (const item of data) {
      const val = item[key];
      if (!val) continue;

      if (value === "00:00:00") {
        sum += timeToSeconds(val);
      } else if (val === value) {
        sum += 1;
      }
    }

    result[key] = value === "00:00:00" ? secondsToTime(sum) : sum;
  }

  return result;
}
//ex data = {data:[{},{}],data2:[{},{}]}
export function sumByGroupKey(dataObj, keysConfig) {
  const timeToSeconds = (timeStr) => {
    const [h, m, s] = timeStr.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  };

  const secondsToTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, "0");
    const m = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const s = (totalSeconds % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const finalResult = {};

  for (const [groupKey, records] of Object.entries(dataObj)) {
    const groupResult = {};

    // Init with 0
    for (const key in keysConfig) {
      groupResult[key] = 0;
    }

    for (const item of records) {
      for (const [key, expected] of Object.entries(keysConfig)) {
        const val = item[key];
        if (val === undefined || val === null) continue;

        if (expected === "00:00:00") {
          // Sum time
          groupResult[key] += timeToSeconds(val);
        } else if (typeof expected === "string") {
          // Count matches of string
          if (val === expected) groupResult[key] += 1;
        } else if (typeof expected === "number") {
          // Sum numbers
          groupResult[key] += Number(val) || 0;
        }
      }
    }

    // Convert time keys back to HH:MM:SS
    for (const [key, expected] of Object.entries(keysConfig)) {
      if (expected === "00:00:00") {
        groupResult[key] = secondsToTime(groupResult[key]);
      }
    }

    finalResult[groupKey] = groupResult;
  }

  return finalResult;
}
